// Drift-compensated send scheduler. It emits `count` ticks spaced at
// 1000/pps ms. Each tick's delay is computed from the absolute planned time of
// the NEXT tick against the current clock, so scheduling error does not
// accumulate over a long run. The per-tick error (actual minus planned, in
// microseconds) is recorded for diagnostics.

/** Injectable timer/clock surface so the pacer is testable. */
export interface PacerEnv {
  /** Monotonic clock in microseconds. */
  now: () => number;
  /** setTimeout returning an opaque handle. */
  setTimeout: (cb: () => void, ms: number) => unknown;
  /** clearTimeout for a handle produced by setTimeout. */
  clearTimeout: (handle: unknown) => void;
}

/** A single emitted tick. */
export interface PacerTick {
  seq: number;
  /** Planned send time in microseconds (from the run start). */
  scheduledUs: number;
  /** Actual clock reading at emission, in microseconds. */
  actualUs: number;
  /** actualUs - scheduledUs, in microseconds. */
  errorUs: number;
}

function defaultEnv(): PacerEnv {
  return {
    now: () => performance.now() * 1000,
    setTimeout: (cb, ms) => setTimeout(cb, ms) as unknown,
    clearTimeout: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
  };
}

/** Drift-compensated pacer. Reusable across runs; state is per run(). */
export class Pacer {
  readonly pps: number;
  /** Nominal interval between ticks, in microseconds. */
  readonly intervalUs: number;
  /** Per-tick error samples (actual - scheduled, microseconds). */
  readonly errorsUs: number[] = [];

  private readonly env: PacerEnv;
  private handle: unknown = null;
  private stopped = false;

  constructor(pps: number, env: PacerEnv = defaultEnv()) {
    this.pps = pps;
    this.intervalUs = pps > 0 ? 1_000_000 / pps : 0;
    this.env = env;
  }

  /** Planned time of tick `i` (microseconds) relative to a run start. */
  scheduledUsAt(i: number, startUs: number): number {
    return startUs + i * this.intervalUs;
  }

  /**
   * Drive `count` ticks, invoking `onTick` for each. Resolves once all ticks
   * have fired or stop() is called. `signal` allows external cancellation.
   */
  run(
    count: number,
    onTick: (tick: PacerTick) => void,
    signal?: { aborted: boolean },
  ): Promise<void> {
    this.stopped = false;
    this.errorsUs.length = 0;

    return new Promise<void>((resolve) => {
      if (count <= 0) {
        resolve();
        return;
      }
      const startUs = this.env.now();
      let i = 0;

      const step = () => {
        if (this.stopped || signal?.aborted) {
          resolve();
          return;
        }
        const scheduledUs = startUs + i * this.intervalUs;
        const actualUs = this.env.now();
        const errorUs = actualUs - scheduledUs;
        this.errorsUs.push(errorUs);
        onTick({ seq: i, scheduledUs, actualUs, errorUs });

        i++;
        if (i >= count) {
          resolve();
          return;
        }
        const nextScheduledUs = startUs + i * this.intervalUs;
        const delayMs = Math.max(0, (nextScheduledUs - this.env.now()) / 1000);
        this.handle = this.env.setTimeout(step, delayMs);
      };

      this.handle = this.env.setTimeout(step, 0);
    });
  }

  /** Cancel an in-flight run(). Safe to call more than once. */
  stop(): void {
    this.stopped = true;
    if (this.handle !== null) {
      this.env.clearTimeout(this.handle);
      this.handle = null;
    }
  }

  /** Mean absolute scheduling error over the last run, in microseconds. */
  meanAbsErrorUs(): number {
    if (this.errorsUs.length === 0) return 0;
    let acc = 0;
    for (const e of this.errorsUs) acc += Math.abs(e);
    return acc / this.errorsUs.length;
  }
}
